import React, { useState, useCallback } from 'react'

import { MapPin } from 'lucide-react'

import musicModalImg from '@/assets/images/modals/modal-music.png'
import { Playlist } from '@/core/api/entities/playlist/playlist'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useAuth } from '@/core/hooks/useAuth'
import {
	useAssignedPlaylists,
	useInUsePlaylists,
	useFavoritePlaylists,
	usePlaylists,
	useAddPlaylistToFavorites,
} from '@/core/hooks/usePlaylist'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { useTranslation } from '@/core/i18n/useTranslation'
import { ActionMenuItem } from '@/shared/components/molecules/ActionDropdownMenu'
import SuccessModal from '@/shared/components/molecules/SuccessModal/SuccessModal'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useWelcomeModal } from '@/shared/hooks/useWelcomeModal'

import { AssignPlaylistModal } from './components/AssignPlaylistModal'
import { useFilteredPlaylistSections } from './hooks/useFilteredPlaylistSections'
import { useProfileCompletion } from './hooks/useProfileCompletion'
import MusicPageUI from './MusicPageUI'

const MusicPage: React.FC = () => {
	const { t } = useTranslation('common')
	const { showNotification } = useNotificationContext()
	const [searchQuery, setSearchQuery] = useState('')
	const debouncedSearchQuery = useDebounce(searchQuery, 500)
	const [isRequestModalOpen, setIsRequestModalOpen] = useState(false)
	const [assignModalState, setAssignModalState] = useState<{
		open: boolean
		playlistId?: number
		playlistName?: string
	}>({ open: false })
	// Welcome modal with localStorage persistence and URL parameter support
	const {
		showModal: showWelcomeModal,
		handleCloseModal: handleCloseWelcomeModal,
	} = useWelcomeModal({
		modalKey: 'music',
	})

	// Pagination state for each carousel
	const [inUsePage, setInUsePage] = useState(1)
	const [assignedPage, setAssignedPage] = useState(1)
	const [favoritesPage, setFavoritesPage] = useState(1)

	// Check profile completion
	const { data: profileCompletion, isLoading: isLoadingProfileCompletion } =
		useProfileCompletion()

	// Get account data for subscription/plan info
	const { user: currentUser } = useAuth()

	const accountType = currentUser?.account?.accountType
	// Memoize params to ensure stable query keys
	const inUsePlaylistsParams = React.useMemo(
		() => ({
			per_page: 5,
			page: inUsePage,
		}),
		[inUsePage]
	)

	const assignedPlaylistsParams = React.useMemo(
		() => ({
			per_page: 5,
			page: assignedPage,
		}),
		[assignedPage]
	)

	const favoritePlaylistsParams = React.useMemo(
		() => ({
			per_page: 5,
			page: favoritesPage,
		}),
		[favoritesPage]
	)

	// Fetch in-use playlists (Assigned playlists)
	const {
		data: inUsePlaylistsData,
		isLoading: isLoadingInUse,
		refetch: refetchInUse,
	} = useInUsePlaylists({
		config: useAccountConfig({
			params: inUsePlaylistsParams,
		}),
	})

	// Fetch assigned playlists (Custom playlists)
	const {
		data: assignedPlaylistsData,
		isLoading: isLoadingAssigned,
		refetch: refetchAssigned,
	} = useAssignedPlaylists({
		config: useAccountConfig({
			params: assignedPlaylistsParams,
		}),
	})

	// Fetch favorite playlists
	const {
		data: favoritePlaylistsData,
		isLoading: isLoadingFavorites,
		refetch: refetchFavorites,
	} = useFavoritePlaylists({
		config: useAccountConfig({
			params: favoritePlaylistsParams,
		}),
	})

	// Progressive loading for filtered sections (moods/genres/business categories)
	const { visibleSections, hasMore, loadMore } = useFilteredPlaylistSections()

	// Memoize search params to ensure stable query keys
	const searchPlaylistsParams = React.useMemo(
		() => ({
			query: debouncedSearchQuery,
			include: 'music_genres',
			per_page: 25,
		}),
		[debouncedSearchQuery]
	)

	// Search playlists
	const { data: searchPlaylistsData, isLoading: isLoadingSearch } =
		usePlaylists({
			config: useAccountConfig({
				params: searchPlaylistsParams,
			}),
			options: {
				enabled: debouncedSearchQuery.length > 0,
			},
		})

	// Track which playlist is currently being favorited/unfavorited
	const [loadingPlaylistId, setLoadingPlaylistId] = useState<number | null>(
		null
	)
	const [isFavoriting, setIsFavoriting] = useState<boolean>(true)

	// Create a Set of favorite playlist IDs for quick lookup (use data directly)
	const favoritePlaylistIds = React.useMemo(() => {
		const favoritePlaylists = favoritePlaylistsData?.data || []
		return new Set(favoritePlaylists.map((playlist) => playlist.id))
	}, [favoritePlaylistsData?.data])

	// Mutation for adding playlist to favorites
	const addToFavoritesMutation = useAddPlaylistToFavorites({
		config: useAccountConfig({}),
		options: {
			onSuccess: (
				_: void,
				variables: { playlistId: number; favorite: boolean }
			) => {
				setLoadingPlaylistId(null)
				refetchFavorites()
				refetchAssigned()
				refetchInUse()
				showNotification({
					message: variables.favorite
						? t('entities.music.playlist_added_to_favorites')
						: t('entities.music.playlist_removed_from_favorites'),
					type: 'success',
				})
			},
			onError: () => {
				setLoadingPlaylistId(null)
				showNotification({
					message: isFavoriting
						? t('entities.music.playlist_add_to_favorites_error')
						: t('entities.music.playlist_remove_from_favorites_error'),
					type: 'error',
				})
			},
		},
	})

	// Handle favorite click
	const handleFavoriteClick = useCallback(
		(playlist: Playlist, isFavoritesCarousel: boolean) => {
			setLoadingPlaylistId(playlist.id)

			// Determine the favorite state based on:
			// 1. If in Favorites carousel → always unfavorite (favorite: false)
			// 2. If in other carousels → toggle based on current favorite status
			let favorite: boolean
			if (isFavoritesCarousel) {
				// In favorites carousel, always unfavorite
				favorite = false
			} else {
				// In other carousels, check if playlist is currently favorited
				const isCurrentlyFavorite = favoritePlaylistIds.has(playlist.id)
				// If already favorited, unfavorite it; otherwise, favorite it
				favorite = !isCurrentlyFavorite
			}

			setIsFavoriting(favorite)
			addToFavoritesMutation.mutate({ playlistId: playlist.id, favorite })
		},
		[addToFavoritesMutation, favoritePlaylistIds]
	)

	// Get pagination metadata
	const inUsePagination = inUsePlaylistsData?.meta?.pagination
	const assignedPagination = assignedPlaylistsData?.meta?.pagination
	const favoritesPagination = favoritePlaylistsData?.meta?.pagination

	// Pagination handlers with wrap-around (only if more than 1 page)
	const handleInUsePageChange = useCallback(
		(direction: 'next' | 'prev') => {
			setInUsePage((prev) => {
				if (!inUsePagination || inUsePagination.total_pages <= 1) {
					return prev
				}
				if (direction === 'next') {
					// If on last page, wrap to first page
					if (prev >= inUsePagination.total_pages) {
						return 1
					}
					return prev + 1
				} else {
					// If on first page, wrap to last page
					if (prev <= 1) {
						return inUsePagination.total_pages
					}
					return prev - 1
				}
			})
		},
		[inUsePagination]
	)

	const handleAssignedPageChange = useCallback(
		(direction: 'next' | 'prev') => {
			setAssignedPage((prev) => {
				if (!assignedPagination || assignedPagination.total_pages <= 1) {
					return prev
				}
				if (direction === 'next') {
					// If on last page, wrap to first page
					if (prev >= assignedPagination.total_pages) {
						return 1
					}
					return prev + 1
				} else {
					// If on first page, wrap to last page
					if (prev <= 1) {
						return assignedPagination.total_pages
					}
					return prev - 1
				}
			})
		},
		[assignedPagination]
	)

	const handleFavoritesPageChange = useCallback(
		(direction: 'next' | 'prev') => {
			setFavoritesPage((prev) => {
				if (!favoritesPagination || favoritesPagination.total_pages <= 1) {
					return prev
				}
				if (direction === 'next') {
					// If on last page, wrap to first page
					if (prev >= favoritesPagination.total_pages) {
						return 1
					}
					return prev + 1
				} else {
					// If on first page, wrap to last page
					if (prev <= 1) {
						return favoritesPagination.total_pages
					}
					return prev - 1
				}
			})
		},
		[favoritesPagination]
	)

	// Handle assign playlist
	const handleAssignPlaylist = useCallback((playlist: Playlist) => {
		setAssignModalState({
			open: true,
			playlistId: playlist.id,
			playlistName: playlist.title,
		})
	}, [])

	// Get dropdown options for playlist cards
	const getDropdownOptions = useCallback(
		(playlist: Playlist): ActionMenuItem[] => {
			return [
				{
					label: t('entities.music.actions.assign'),
					icon: MapPin,
					onClick: () => handleAssignPlaylist(playlist),
				},
			]
		},
		[t, handleAssignPlaylist]
	)

	// Determine which playlists to show
	const showSearchResults = debouncedSearchQuery.length > 0
	const inUsePlaylists = inUsePlaylistsData?.data || []
	const assignedPlaylists = assignedPlaylistsData?.data || []
	const favoritePlaylists = favoritePlaylistsData?.data || []
	const searchPlaylists = searchPlaylistsData?.data || []

	// Check if initial data is still loading (profile completion + initial playlists)
	const isInitialLoading =
		isLoadingProfileCompletion ||
		isLoadingAssigned ||
		isLoadingFavorites ||
		isLoadingInUse

	return (
		<>
			<MusicPageUI
				isInitialLoading={isInitialLoading}
				searchQuery={searchQuery}
				onSearchChange={setSearchQuery}
				inUsePlaylists={inUsePlaylists}
				assignedPlaylists={assignedPlaylists}
				favoritePlaylists={favoritePlaylists}
				filteredSections={visibleSections}
				hasMoreFilteredSections={hasMore}
				onLoadMoreFilteredSections={loadMore}
				searchPlaylists={searchPlaylists}
				isLoadingInUse={isLoadingInUse}
				isLoadingAssigned={isLoadingAssigned}
				isLoadingFavorites={isLoadingFavorites}
				isLoadingSearch={isLoadingSearch}
				showSearchResults={showSearchResults}
				onFavoriteClick={handleFavoriteClick}
				loadingPlaylistId={loadingPlaylistId}
				onRequestCustomPlaylistClick={() => setIsRequestModalOpen(true)}
				isRequestModalOpen={isRequestModalOpen}
				onCloseRequestModal={() => setIsRequestModalOpen(false)}
				accountType={accountType}
				profileIncomplete={!profileCompletion?.isComplete}
				inUsePagination={inUsePagination}
				assignedPagination={assignedPagination}
				favoritesPagination={favoritesPagination}
				onInUsePageChange={handleInUsePageChange}
				onAssignedPageChange={handleAssignedPageChange}
				onFavoritesPageChange={handleFavoritesPageChange}
				favoritePlaylistIds={favoritePlaylistIds}
				accountId={currentUser?.accountId}
				getDropdownOptions={getDropdownOptions}
			/>

			<AssignPlaylistModal
				open={assignModalState.open}
				onOpenChange={(open) =>
					setAssignModalState((prev) => ({ ...prev, open }))
				}
				playlistId={assignModalState.playlistId}
				playlistName={assignModalState.playlistName}
			/>

			<SuccessModal
				visible={showWelcomeModal}
				image={musicModalImg}
				imageAlt={t('welcomeModals.music.title')}
				title={t('welcomeModals.music.title')}
				description={t('welcomeModals.music.description')}
				primaryAction={{
					label: t('welcomeModals.music.continue'),
					onClick: handleCloseWelcomeModal,
				}}
				onClose={handleCloseWelcomeModal}
			/>
		</>
	)
}

export default MusicPage
